import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  PlayCircleIcon,
  CheckIcon,
  MinusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import {
  QualifyingStageChip,
  getVerdictMeta,
  ruledOutReason,
  getQualifyingScoreTone,
  callOutcomeText,
  callOutcomeTextClass,
  scheduledCallLabel,
  nextCallReasonLabel,
} from './qualifyingAgentStatus';

/**
 * The quick card the Status chip opens on a qualifying-only screening (22 Oct): where the
 * candidate is and what they said, readable in a few seconds.
 *
 *   header   name, stage, score; a progress bar; the last call and the next one as two tiles
 *   Interest the intro and role brief, one line each — the candidate's yes/no, not the script
 *            (the recruiter wrote those lines; reading them back here was most of the card)
 *   Answers  the recruiter's questions in call order, numbered, each with its answer or
 *            "Not answered yet" in place, so a gap shows where it is
 *
 * The full replay modal (transcript, recordings, per-answer ratings) is one click away.
 */

const SCORE_CLASSES = {
  gray: 'text-gray-400',
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-600',
};

// The script lines before the recruiter's own questions (QualifyingAgentQuestionsEditor).
const OPENING_LABELS = { intro: 'Interested in applying', willingness: 'Role sounds exciting' };

function answerText(q) {
  if (q.status === 'not_answered') return '';
  return q.summary || q.answer || '';
}

/** answered / partial / declined / waiting, for the row's marker and tag. */
function answerState(q) {
  if (!answerText(q)) return 'waiting';
  if (q.status === 'declined') return 'declined';
  if (q.status === 'partial' || (q.missingParts || []).length > 0) return 'partial';
  return 'answered';
}

const MARKER_CLASSES = {
  answered: 'bg-green-50 text-green-700 ring-green-200',
  partial: 'bg-amber-50 text-amber-700 ring-amber-200',
  declined: 'bg-red-50 text-red-600 ring-red-200',
  waiting: 'bg-white text-gray-400 ring-gray-200',
};

function ChannelIcon({ channel }) {
  return channel === 'whatsapp' ? (
    <ChatBubbleLeftRightIcon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-green-600" title="Answered on WhatsApp" />
  ) : (
    <PhoneIcon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[#007BFF]" title="Answered on the call" />
  );
}

function SectionLabel({ children }) {
  return <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{children}</p>;
}

/** "Interested in applying — Yes." The question itself is in the tooltip. */
function OpeningRow({ q }) {
  const state = answerState(q);
  const text = answerText(q);
  const Icon = state === 'waiting' ? MinusIcon : state === 'declined' ? XMarkIcon : CheckIcon;
  return (
    <li className="flex items-start gap-2.5 py-1.5" title={q.prompt}>
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ${MARKER_CLASSES[state]}`}>
        <Icon className="h-3 w-3" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium text-gray-500">{OPENING_LABELS[q.kind] || 'Intro'}</p>
        <p className={`text-[14px] leading-snug ${text ? 'font-medium text-gray-900' : 'italic text-gray-400'}`}>
          {text || 'No reply yet'}
        </p>
      </div>
      {text && <ChannelIcon channel={q.channel} />}
    </li>
  );
}

function AnswerRow({ q, number }) {
  const state = answerState(q);
  const text = answerText(q);
  return (
    <li className="flex items-start gap-2.5 py-2" data-state={state}>
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ring-1 ${MARKER_CLASSES[state]}`}
      >
        {state === 'answered' ? <CheckIcon className="h-3 w-3" strokeWidth={2.5} /> : number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] leading-snug text-gray-500">{q.prompt}</p>
        {text ? (
          <p className="mt-0.5 flex items-start gap-1.5 text-[14px] font-medium leading-snug text-gray-900">
            <span className="min-w-0 flex-1 whitespace-pre-line">{text}</span>
            <ChannelIcon channel={q.channel} />
          </p>
        ) : (
          <p className="mt-0.5 text-[13px] italic text-gray-400">Not answered yet</p>
        )}
        {state === 'partial' && (
          <p className="mt-1 text-[11px] font-medium text-amber-700">
            Partly answered{q.missingParts?.length ? ` — still needed: ${q.missingParts.join(', ')}` : ''}
          </p>
        )}
        {state === 'declined' && <p className="mt-1 text-[11px] font-medium text-red-600">Declined to answer</p>}
      </div>
    </li>
  );
}

function InfoTile({ label, value, hint, valueClass = 'text-gray-800' }) {
  return (
    <div className="min-w-0 flex-1 rounded-lg bg-gray-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`truncate text-[12px] font-semibold ${valueClass}`} title={value}>
        {value}
      </p>
      {hint && (
        <p className="truncate text-[11px] text-gray-500" title={hint}>
          {hint}
        </p>
      )}
    </div>
  );
}

export default function QualifyingAnswersCard({ screeningId, candidate, onClose, onOpenFull }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const candidateId = candidate?.id || candidate?._id;
  const qualifying = candidate?.qualifying || null;
  const hasSession = !!qualifying;

  // Read once when the card opens — keyed on whether a session exists, not on the row's
  // `qualifying` object, so a table refresh that replaces that object does not re-fetch.
  useEffect(() => {
    if (!screeningId || !candidateId || !hasSession) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`/api/screenings/${screeningId}/qualifying-agent-session/${candidateId}`)
      .then((res) => {
        if (!cancelled) setSession(res.data?.session || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load the answers');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateId, hasSession]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = candidate?.name || session?.candidateName || 'Candidate';
  const questions = session?.perQuestion || [];
  const opening = questions.filter((q) => OPENING_LABELS[q.kind]);
  const own = questions.filter((q) => !OPENING_LABELS[q.kind]);
  const verdict = getVerdictMeta(qualifying);
  const requirementNotMet = ruledOutReason(qualifying) === 'requirement';
  const score = session?.overallScore ?? qualifying?.overallScore ?? null;
  const total = session?.totalQuestions ?? qualifying?.totalQuestions ?? 0;
  const answeredCount = session?.answeredCount ?? qualifying?.answeredCount ?? 0;
  const percent = total > 0 ? Math.round((Math.min(answeredCount, total) / total) * 100) : 0;
  // The outcome alone: the answered count is the progress bar's job.
  const lastCall = callOutcomeText(qualifying?.lastCall, 0);
  const nextCall = scheduledCallLabel(qualifying);
  const askedByCandidate = session?.unansweredQuestions || [];
  const ownAnswered = own.filter((q) => answerText(q)).length;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${name} — answers`}
        data-testid="qualifying-answers-card"
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-lg max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="border-b border-gray-100 px-5 pb-4 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-slate-900">{name}</h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <QualifyingStageChip qualifying={qualifying} />
                {score != null && (
                  <span className={`text-sm font-bold tabular-nums ${SCORE_CLASSES[getQualifyingScoreTone(score)]}`}>
                    {score}% fit
                  </span>
                )}
                {verdict && (
                  <span className={`text-xs font-semibold ${SCORE_CLASSES[verdict.tone] || SCORE_CLASSES.gray}`}>
                    {verdict.label}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {qualifying && total > 0 && (
            <div className="mt-3" data-testid="qualifying-answers-progress">
              <div className="mb-1 flex items-baseline justify-between text-[11px]">
                <span className="font-semibold text-gray-700">
                  {Math.min(answeredCount, total)} of {total} answered
                </span>
                <span className="tabular-nums text-gray-400">{percent}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full ${percent === 100 ? 'bg-green-500' : 'bg-[#007BFF]'}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}

          {(lastCall || nextCall) && (
            <div className="mt-3 flex gap-2">
              {lastCall && (
                <InfoTile label="Last call" value={lastCall} valueClass={callOutcomeTextClass(qualifying?.lastCall?.outcome)} />
              )}
              {nextCall && <InfoTile label="Next call" value={nextCall} hint={nextCallReasonLabel(qualifying)} />}
            </div>
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {!qualifying ? (
            <p className="py-10 text-center text-sm text-gray-500">
              The Qualifying Agent has not contacted this candidate yet.
            </p>
          ) : loading ? (
            <div className="flex justify-center py-10">
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#007BFF]/60 border-t-transparent" />
            </div>
          ) : error ? (
            <p className="py-10 text-center text-sm text-red-600">{error}</p>
          ) : (
            <div className="space-y-5">
              {qualifying.knockout?.note && (
                <p
                  className={`rounded-lg border px-3 py-2 text-[13px] ${
                    requirementNotMet ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-red-100 bg-red-50 text-red-700'
                  }`}
                >
                  {qualifying.knockout.note}
                </p>
              )}

              {opening.length > 0 && (
                <section>
                  <SectionLabel>Interest</SectionLabel>
                  <ul className="divide-y divide-gray-50" data-testid="qualifying-opening-list">
                    {opening.map((q) => (
                      <OpeningRow key={q.index} q={q} />
                    ))}
                  </ul>
                </section>
              )}

              {own.length > 0 && (
                <section>
                  <SectionLabel>
                    Answers · {ownAnswered} of {own.length}
                  </SectionLabel>
                  <ol className="divide-y divide-gray-50" data-testid="qualifying-answers-list">
                    {own.map((q, i) => (
                      <AnswerRow key={q.index} q={q} number={i + 1} />
                    ))}
                  </ol>
                </section>
              )}

              {opening.length === 0 && own.length === 0 && (
                <p className="py-6 text-center text-sm text-gray-500">No answers yet.</p>
              )}

              {askedByCandidate.length > 0 && (
                <section className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-800">
                    <QuestionMarkCircleIcon className="h-4 w-4" />
                    The candidate asked — needs your answer
                  </p>
                  <ul className="mt-1 space-y-0.5 pl-5">
                    {askedByCandidate.map((q, i) => (
                      <li key={`${i}-${q.text}`} className="text-[12px] text-amber-900">
                        “{q.text}”
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>

        {qualifying && typeof onOpenFull === 'function' && (
          <footer className="flex justify-end border-t border-gray-100 px-5 py-3">
            <button
              type="button"
              onClick={onOpenFull}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-[#007BFF]/5 px-3 py-1.5 text-xs font-semibold text-[#0062CC] hover:bg-[#007BFF]/10"
            >
              <PlayCircleIcon className="h-4 w-4" />
              Full transcript & recording
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
